// Hydraulic system schema (v2)
// The top-level document structure
// ADR-0003: Graph-Based Structure with ID References

import { z } from 'zod';
import { FluidZoneSchema } from './document/fluid-zone';
import { PipeSpecificationSchema } from './document/pipe-specification';
import { CalculationSettingsSchema } from './document/calculation-settings';
import { DesignConstraintsSchema } from './document/design-constraints';
import { DocumentHeaderSchema } from './document/document-header';
import { UnitSystemSchema } from './document/unit-system';
import { HeatTransferSettingsSchema } from './document/heat-transfer-settings';
import { LayerSchema } from './document/layer';
import { AnnotationSchema } from './document/annotation';
import { GradientColorsSchema } from './document/gradient-colors';
import { GroupSchema } from './document/group';
import { CustomFieldDefinitionSchema } from './document/custom-field-definition';
import { VertexSchema } from './vertices';
import { EdgeSchema } from './edges';
import { DiagramElementSchema } from './diagram-elements';
import { ScenariosSchema } from './scenarios/schema';
import { validateScenariosClosure } from './scenarios/closure';
import type { ScenarioClosureInput } from './scenarios/closure';

/**
 * Hydraulic System Schema (v2)
 *
 * The complete document structure representing a hydraulic flow analysis system.
 *
 * Design principles:
 * - ADR-0003: Graph-based structure (vertices + edges with ID references)
 * - Normalized document-level data (fluid zones, pipe specs) referenced by ID
 * - Collections are stored as arrays (position in array doesn't matter)
 * - IDs are used for all cross-references
 */
/**
 * The version PF-connect WRITES.
 *
 * v3 exists because `calculation_result.status` gained `partial`. A document
 * carrying `partial` is not valid against a v2 reader's enum, so stamping it v2
 * strands it: the version says "you can read this", the content says otherwise,
 * and the reader rejects the whole document with an opaque enum error. Bumping
 * makes the version honest — an older build refuses it at the version check, with
 * a message that says to upgrade, and the document stays migratable.
 */
export const CURRENT_SCHEMA_VERSION = 3;

/**
 * The versions PF-connect READS.
 *
 * v2 is still accepted, unchanged: every document already saved is v2, and
 * `migrateToLatest` is not wired into any load path (see migrations/index.ts), so
 * narrowing this to v3 alone would reject every existing model. v2 documents are
 * forward-compatible by construction — v3 adds only an enum member — so reading
 * them needs no transformation.
 */
export const SUPPORTED_SCHEMA_VERSIONS = [2, 3] as const;

/**
 * The calculation statuses the PERSISTED document schema accepts, derived from
 * `HydraulicSystemSchema` below rather than restated here.
 */
type CalculationStatus = NonNullable<NonNullable<HydraulicSystem['calculation_result']>['status']>;

/**
 * The schema version each calculation status FIRST became legal in.
 *
 * These are historical facts, not the current version, which is why they are
 * literals: `CURRENT_SCHEMA_VERSION` would silently rewrite them on the next
 * bump (v4 would start claiming `partial` needs v4 and reject the v3 documents
 * we ourselves wrote). The map is exhaustive over the schema's own status union,
 * so adding a status to `calculation_result` fails this file's type-check until
 * its introducing version is recorded here — a later bump cannot leave the guard
 * behind silently.
 */
const STATUS_INTRODUCED_IN: Readonly<Record<CalculationStatus, number>> = {
  // Present since v2, the oldest version this application reads.
  success: 2,
  failed: 2,
  invalid_topology: 2,
  invalid_input: 2,
  // The reason v3 exists (see CURRENT_SCHEMA_VERSION above).
  partial: 3,
};

/**
 * Reports the ONE incoherence the version bump exists to remove: a document
 * whose `schema_version` predates a `calculation_result.status` it carries.
 * Such a document lies — the stamp promises an older reader can read it, the
 * content makes that false, and that reader fails on an opaque enum error
 * instead of "upgrade to open this".
 *
 * Deliberately NOT full `HydraulicSystemSchema` validation: the persistence
 * boundary that calls this has always saved whatever it was handed, so anything
 * else that is off stays saved exactly as it is today. Returns the caller-facing
 * message, or null when the document is coherent (including when it carries no
 * status at all).
 */
export function incoherentStatusForSchemaVersion(system: {
  schema_version?: unknown;
  calculation_result?: { status?: unknown } | null;
}): string | null {
  const version = system.schema_version;
  const status = system.calculation_result?.status;
  if (typeof version !== 'number' || typeof status !== 'string') return null;
  const introducedIn = STATUS_INTRODUCED_IN[status as CalculationStatus];
  if (introducedIn === undefined || version >= introducedIn) return null;
  return (
    `system.calculation_result.status '${status}' requires schema_version ${introducedIn} or later ` +
    `(system.schema_version is ${version}); re-save from an up-to-date client, ` +
    `or stamp the document schema_version ${introducedIn}`
  );
}

export const HydraulicSystemSchema = z.object({
  schema_version: z.union([z.literal(2), z.literal(3)]),

  // Document-level configuration
  calculation_settings: CalculationSettingsSchema,

  // Normalized reference data
  fluid_zones: z.array(FluidZoneSchema),
  pipe_specifications: z.array(PipeSpecificationSchema),

  // Graph structure (ADR-0003: Vertices = devices, Edges = pipes/ducts)
  vertices: z.array(VertexSchema),
  edges: z.array(EdgeSchema),

  // --- v2: optional document-level features ---
  document_header: DocumentHeaderSchema.optional(),
  unit_system: UnitSystemSchema.optional(),
  heat_transfer_settings: z.array(HeatTransferSettingsSchema).optional(),
  layers: z.array(LayerSchema).optional(),
  annotations: z.array(AnnotationSchema).optional(),
  diagram_elements: z.array(DiagramElementSchema).default([]),
  gradient_colors: GradientColorsSchema.optional(),

  /**
   * How far apart the drawing has been spread, relative to the layout as imported/authored.
   * 1 = untouched. Purely a layout record: device positions ARE rewritten when this changes,
   * so nothing at render time reads it.
   *
   * ⚠️ Stored so the spread control is idempotent and survives a reload. Without it the
   * slider would reset to 1 on reopen while the model stayed spread, and the next drag would
   * compound instead of setting an absolute spread. It is NOT a display scale and must never
   * be applied on top of the positions — they already include it.
   */
  diagram_spacing: z.number().positive().optional(),

  /** Named, user-facing element groups (spec §5). One "Group N" group is
   * auto-created per connected component on import; editable thereafter.
   * Decoupled from the solver — a group may span solve components or vice-versa,
   * which is why these are Groups and not the solver's "System N". */
  groups: z.array(GroupSchema).default([]),
  custom_field_definitions: z.array(CustomFieldDefinitionSchema).optional(),

  /** AFT-style scenario tree (parent/child override layers). Base = the live
   * vertices/edges; child nodes store only sparse deltas. See scenarios/. */
  scenarios: ScenariosSchema.optional(),

  /** Deferred / opaque types (stored for round-trip fidelity) */
  device_graphs: z.array(z.unknown()).optional(),
  custom_symbols: z.array(z.unknown()).optional(),
  data_automation: z.unknown().optional(),
  data_import: z.unknown().optional(),

  /** Diagram zoom level */
  shape_zoom: z.number().optional(),

  /**
   * Opaque overtime configuration block.
   * Serialized as std::string AFTER the main Boost archive (file version >= 16).
   * The converter stores this as-is for round-trip fidelity.
   * Contains its own internal serialization that this schema does not interpret.
   */
  overtime_block: z.string().optional(),

  /** Solver calculation result metadata */
  calculation_result: z.object({
    status: z.enum(['success', 'failed', 'partial', 'invalid_topology', 'invalid_input']).optional(),
    timestamp: z.string().optional(),
    iterations: z.number().optional(),
    convergence: z.number().optional(),
  }).optional(),

  /** User-defined design constraints for post-calculation validation */
  design_constraints: DesignConstraintsSchema.optional(),

  application_type: z.string().optional(),

}).refine((data) => {
  // Validate referential integrity: edge connections must reference valid vertices
  const vertexIds = new Set(data.vertices.map(v => v.id));
  return data.edges.every(e =>
    vertexIds.has(e.connection.source_id) &&
    vertexIds.has(e.connection.target_id)
  );
}, {
  message: 'All edge connections must reference valid vertex IDs',
}).refine((data) => {
  // Validate referential integrity: edges that reference a fluid zone must reference a valid one.
  // Duct edges may have an unset fluid_zone_id (DuctEdgeSchema makes it optional, mirroring
  // the C++ duct_design::as_json is_set() pattern). When the field is absent we skip the check;
  // when present we still require it to resolve.
  const fluidZoneIds = new Set(data.fluid_zones.map(fz => fz.id));
  return data.edges.every(e =>
    e.fluid_zone_id === undefined || fluidZoneIds.has(e.fluid_zone_id)
  );
}, {
  message: 'All edges must reference valid fluid zone IDs',
}).refine((data) => {
  // Validate referential integrity: pipe/compressible_pipe edges must reference valid pipe specifications
  const pipeSpecIds = new Set(data.pipe_specifications.map(ps => ps.id));
  return data.edges.every(e => {
    if (e.type === 'pipe' || e.type === 'compressible_pipe') {
      return pipeSpecIds.has(e.pipe_specification_id);
    }
    return true; // duct doesn't reference pipe specs
  });
}, {
  message: 'Pipe/compressible_pipe edges must reference valid pipe specification IDs',
}).refine((data) => {
  // Validate referential integrity: tank penetrations must reference valid edges
  const edgeIds = new Set(data.edges.map(e => e.id));
  return data.vertices.every(v => {
    if (v.type === 'tank') {
      return v.penetrations.every(p => edgeIds.has(p.edge_id));
    }
    return true;
  });
}, {
  message: 'All tank penetrations must reference valid edge IDs',
}).refine((data) => validateScenariosClosure(data as unknown as ScenarioClosureInput).length === 0, {
  message: 'Scenario overrides must reference valid elements and allowed fields',
});

export type HydraulicSystem = z.infer<typeof HydraulicSystemSchema>;
